/**
 * ChatDock — กล่องแชทลอย อยู่ทุกหน้า
 *
 * ปุ่มกลมมุมขวาล่าง มีตัวเลขบอกจำนวนข้อความที่ยังไม่อ่าน กดแล้วเปิดกล่องคุยได้เลย
 * ไม่ต้องออกจากงานที่ทำอยู่
 *
 * ข้อความใหม่ต้องดึงความสนใจ ไม่งั้นไม่มีใครอ่าน:
 *   การ์ดเด้งค้างมุมจอจนกว่าจะกด · เสียงติ๊ง · ปุ่มสั่นและเป็นสีแดง
 *   ชื่อแท็บกะพริบตอนเปิดแท็บอื่น · แจ้งเตือนบนจอคอมตอนย่อเบราว์เซอร์ (ต้องกดอนุญาตครั้งแรก)
 *
 * ใช้ของร่วมกับหน้าแชทเต็มจอ (/chat) ที่ components/chat/chatShared.jsx
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  MessageSquare, X, Send, Paperclip, ArrowLeft, Maximize2, ChevronDown, FileText, Bell, BellOff, Volume2, VolumeX,
} from "lucide-react";
import {
  playDing, flashTitle, stopFlash, isMuted, setMuted, desktopNotify,
  desktopPermission, askDesktopPermission,
} from "./chatAlert";
import socket from "../../socket";
import {
  useGetChatRoomsQuery,
  useGetChatRoomQuery,
  useMarkChatReadMutation,
  useDeleteChatMessageMutation,
} from "../../slices/chatApiSlice";
import {
  RoomAvatar, MessageList, fileSize, postMessage, useRoomMessages, useFileDrop, DropOverlay,
  ReplyBar, MentionTextarea, mentionIdsIn, EmojiPicker, T, useStickToBottom } from "./chatShared";
import { useLang } from "../../utils/useTx";
import { HeaderSlotPortal, HEADER_PANEL_POS, headerIconBtn } from "../ui/HeaderSlot";

export default function ChatDock() {
  useLang(); // วาดใหม่เมื่อกดสลับภาษา EN/ไทย — ข้อความในกล่องใช้ T() จาก chatShared (23 ต.ค. 69)
  const { userInfo } = useSelector((s) => s.auth);
  const location = useLocation();
  const navigate = useNavigate();
  const myId = Number(userInfo?._id ?? userInfo?.id ?? 0);

  const [open, setOpen] = useState(false);
  const [roomId, setRoomId] = useState(0);
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [mentioned, setMentioned] = useState(() => new Set());
  const { dragging, dropProps, onPaste, add: addFiles } = useFileDrop(setFiles, { onError: (m) => toast.error(m) });
  const bottomRef = useRef(null);
  const dockListRef = useRef(null);
  // การ์ดเด้งมุมจอ — ค้างไว้จนกว่าจะกดอ่านหรือกดปิด (ไม่หายเองเหมือนแถบแจ้งเตือนธรรมดา)
  const [peeks, setPeeks] = useState([]);
  const [shake, setShake] = useState(false);
  const [muted, setMutedState] = useState(isMuted);
  const [notifyPerm, setNotifyPerm] = useState(desktopPermission);
  const [socketUp, setSocketUp] = useState(socket.connected);
  const unreadRef = useRef(null);   // จำนวนยังไม่อ่านรอบก่อน ต่อห้อง — ไว้จับว่ามีของใหม่

  // อยู่หน้าแชทเต็มจออยู่แล้ว ไม่ต้องมีกล่องลอยซ้อน
  const onChatPage = location.pathname.startsWith("/chat");
  const enabled = Boolean(userInfo) && !onChatPage;

  // socket ต่อติด = ของใหม่มาเอง เช็กห่าง ๆ พอ · ต่อไม่ติด = เช็กเองทุก 10 วินาที
  useEffect(() => {
    const up = () => setSocketUp(true);
    const down = () => setSocketUp(false);
    socket.on("connect", up);
    socket.on("disconnect", down);
    return () => { socket.off("connect", up); socket.off("disconnect", down); };
  }, []);

  const { data, refetch } = useGetChatRoomsQuery(undefined, {
    skip: !enabled,
    pollingInterval: socketUp ? 30000 : 10000,
  });
  const rooms = useMemo(() => data?.rooms || [], [data]);
  const totalUnread = useMemo(() => rooms.reduce((s, r) => s + (Number(r.unread) || 0), 0), [rooms]);
  const room = rooms.find((r) => r.id === roomId) || null;
  // สมาชิกห้องที่เปิดอยู่ — ไว้แท็ก @ และทำชื่อที่ถูกแท็กให้เด่น
  const { data: roomDetail, refetch: refetchDetail } = useGetChatRoomQuery(roomId, { skip: !enabled || !open || !roomId });
  // มีคนอ่าน → โหลดสมาชิกใหม่ ("อ่านแล้ว" ใต้ข้อความของเรา · 19 ต.ค. 69)
  useEffect(() => {
    if (!enabled || !open || !roomId) return undefined;
    const onRead = (e) => { if (Number(e?.room_id) === Number(roomId)) refetchDetail(); };
    socket.on("chat:read", onRead);
    return () => socket.off("chat:read", onRead);
  }, [enabled, open, roomId, refetchDetail]);
  const members = useMemo(() => roomDetail?.members || [], [roomDetail]);
  const memberNames = useMemo(() => members.map((m) => m.name).filter(Boolean), [members]);
  useEffect(() => { setReplyTo(null); setMentioned(new Set()); }, [roomId]);

  const [markRead] = useMarkChatReadMutation();
  const [deleteMessage] = useDeleteChatMessageMutation();
  const { messages, loading, hasMore, loadOlder, appendMine, markDeleted, lastId } = useRoomMessages(
    open && roomId ? roomId : 0,
    { onError: (m) => toast.error(m) },
  );

  // socket แจ้งว่ามีข้อความ — ดึงรายการห้องใหม่ทันที แล้วให้ตัวจับข้างล่างตัดสินว่าต้องเด้งไหม
  useEffect(() => {
    if (!enabled) return undefined;
    const onMessage = () => refetch();
    socket.on("chat:message", onMessage);
    return () => socket.off("chat:message", onMessage);
  }, [enabled, refetch]);

  // จับของใหม่จาก "จำนวนยังไม่อ่านเพิ่มขึ้น" — ใช้ได้ทั้งตอน socket ติดและตอนดึงเองเป็นรอบ
  useEffect(() => {
    if (!enabled || !data) return;
    const prev = unreadRef.current;
    const next = new Map(rooms.map((r) => [r.id, Number(r.unread) || 0]));
    unreadRef.current = next;
    if (prev === null) {
      // เพิ่งเปิดเว็บ — มีค้างอ่านก็แค่ให้ปุ่มสั่นเตือน ไม่เด้งการ์ดย้อนหลังทั้งหมด
      if (rooms.some((r) => r.unread > 0)) { setShake(true); setTimeout(() => setShake(false), 1600); }
      return;
    }
    const fresh = rooms.filter((r) => (next.get(r.id) || 0) > (prev.get(r.id) || 0) && !(open && r.id === roomId));
    if (!fresh.length) return;

    setPeeks((ps) => {
      const others = ps.filter((p) => !fresh.some((f) => f.id === p.roomId));
      const added = fresh.map((r) => ({
        roomId: r.id,
        name: r.name,
        kind: r.kind,
        peerId: r.peer?.id,
        peer: r.peer,
        avatarUrl: r.avatar_url,
        by: r.preview?.by || r.name,
        text: r.preview?.text || T("ส่งข้อความมา", "sent a message"),
        unread: r.unread,
        at: Date.now(),
      }));
      return [...added, ...others].slice(0, 3);
    });
    setShake(true);
    setTimeout(() => setShake(false), 1600);
    playDing();
    if (document.hidden) {
      const f = fresh[0];
      desktopNotify({
        title: `💬 ${f.preview?.by || f.name}`,
        body: String(f.preview?.text || T("ส่งข้อความมา", "sent a message")).slice(0, 120),
        onClick: () => { setRoomId(f.id); setOpen(true); },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // ชื่อแท็บกะพริบตอนไปเปิดแท็บอื่น — กลับมาหน้านี้แล้วหยุด
  useEffect(() => {
    if (!enabled) { stopFlash(); return undefined; }
    const sync = () => {
      if (document.hidden && totalUnread > 0) flashTitle(totalUnread);
      else stopFlash();
    };
    sync();
    document.addEventListener("visibilitychange", sync);
    return () => document.removeEventListener("visibilitychange", sync);
  }, [enabled, totalUnread]);

  const openPeek = (p) => {
    setPeeks((ps) => ps.filter((x) => x.roomId !== p.roomId));
    setRoomId(p.roomId);
    setOpen(true);
  };
  const toggleMute = () => { setMuted(!muted); setMutedState(!muted); };
  const enableDesktop = async () => setNotifyPerm(await askDesktopPermission());

  useStickToBottom(dockListRef, bottomRef, [messages.length, open, roomId]);

  useEffect(() => {
    if (open && roomId && lastId) markRead({ id: roomId, last_id: lastId });
    if (open && roomId) setPeeks((ps) => ps.filter((p) => p.roomId !== roomId));
  }, [open, roomId, lastId, markRead]);

  if (!enabled) return null;

  // quick = ข้อความตอบด่วนจากปุ่มอิโมจิ — ส่งเลย ไม่แตะสิ่งที่พิมพ์ค้าง/ไฟล์ที่แนบไว้
  const send = async (quick) => {
    const q = typeof quick === "string" ? quick : null;
    if (sending || !roomId || (!q && !text.trim() && !files.length)) return;
    setSending(true);
    try {
      const m = await postMessage(roomId, q
        ? { body: q, files: [], replyToId: replyTo?.id }
        : { body: text.trim(), files, replyToId: replyTo?.id, mentionIds: mentionIdsIn(text, mentioned, members) });
      appendMine(m);
      if (!q) {
        setText("");
        setFiles([]);
        setMentioned(new Set());
      }
      setReplyTo(null);
      refetch();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSending(false);
    }
  };

  const doDelete = async (m) => {
    try {
      await deleteMessage(m.id).unwrap();
      markDeleted(m.id);
    } catch (e) {
      toast.error(e?.data?.message || T("ลบไม่สำเร็จ", "Could not delete"));
    }
  };

  return (
    <>
      <style>{`
        @keyframes chatShake { 0%,100%{transform:rotate(0)} 15%{transform:rotate(-16deg) scale(1.12)} 30%{transform:rotate(14deg) scale(1.12)} 45%{transform:rotate(-10deg)} 60%{transform:rotate(8deg)} 75%{transform:rotate(-4deg)} }
        @keyframes chatPeekIn { from{opacity:0; transform:translateY(12px) scale(.96)} to{opacity:1; transform:none} }
        .chat-shake{animation:chatShake .8s ease-in-out 2}
        .chat-peek{animation:chatPeekIn .25s ease-out}
        @media (prefers-reduced-motion: reduce){ .chat-shake,.chat-peek{animation:none} }
      `}</style>

      {/* การ์ดเด้ง — ใครทักมา ข้อความว่าอะไร ค้างไว้จนกว่าจะกด */}
      {!open && peeks.length > 0 && (
        <div className={`${HEADER_PANEL_POS} w-[min(20rem,calc(100vw-1.5rem))] space-y-2`}>
          {peeks.map((p) => (
            <div
              key={p.roomId}
              role="alert"
              className="chat-peek rounded-2xl bg-white shadow-xl ring-1 ring-slate-200 p-3 flex gap-2.5 cursor-pointer hover:ring-indigo-300"
              onClick={() => openPeek(p)}
            >
              <RoomAvatar room={{ kind: p.kind, name: p.name, peer: p.peer || { id: p.peerId }, avatar_url: p.avatarUrl }} size="w-9 h-9" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="text-[13px] font-black text-slate-900 truncate">{p.by}</span>
                  {p.kind === "GROUP" && <span className="text-[10px] text-slate-400 truncate">{T("ใน", "in")} {p.name}</span>}
                </div>
                <div className="text-[13px] text-slate-700 line-clamp-2 break-words">{p.text}</div>
                <div className="mt-1.5 flex items-center gap-2">
                  <span className="rounded-lg bg-slate-900 px-2.5 py-1 text-[11px] font-bold text-white">{T("ตอบ", "Reply")}</span>
                  {p.unread > 1 && <span className="text-[11px] font-semibold text-rose-600">{p.unread} {T("ข้อความใหม่", "new messages")}</span>}
                </div>
              </div>
              <button
                type="button"
                title={T("ปิด (ข้อความยังอยู่ในแชท)", "Close (the message stays in chat)")}
                onClick={(e) => { e.stopPropagation(); setPeeks((ps) => ps.filter((x) => x.roomId !== p.roomId)); }}
                className="self-start p-1 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ปุ่มบนแถบหัวเว็บ (เดิมเป็นปุ่มลอยมุมขวาล่าง · ย้าย 20 ต.ค. 69) — มีค้างอ่านจะมีวงแดงกระเพื่อม · ของใหม่เข้ามาจะสั่น */}
      <HeaderSlotPortal>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-pressed={open}
          title={totalUnread ? T(`มีข้อความใหม่ ${totalUnread} ข้อความ`, `${totalUnread} new messages`) : T("แชทภายใน", "Team chat")}
          className={`${headerIconBtn} order-3 ${
            totalUnread ? "bg-rose-600 hover:bg-rose-700 text-white border-rose-600" : open ? "bg-slate-800 text-white border-slate-800" : "bg-slate-100 hover:bg-slate-200 text-slate-600 border-slate-200"
          } ${shake ? "chat-shake" : ""}`}
        >
          {totalUnread > 0 && <span className="absolute inset-0 rounded-full bg-rose-500 opacity-50 animate-ping motion-reduce:animate-none" />}
          <MessageSquare className="relative w-4 h-4" strokeWidth={2.2} />
          {totalUnread > 0 && (
            <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] px-1 rounded-full bg-white text-rose-600 text-[10px] font-black flex items-center justify-center ring-1 ring-rose-600">
              {totalUnread > 99 ? "99+" : totalUnread}
            </span>
          )}
        </button>
      </HeaderSlotPortal>

      {/* กล่องแชท */}
      {open && (
        <div
          {...(roomId ? dropProps : {})}
          className={`${HEADER_PANEL_POS} w-[min(22rem,calc(100vw-1.5rem))] h-[min(32rem,calc(100dvh-6rem))] flex flex-col rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200 overflow-hidden`}
        >
          <DropOverlay show={dragging} />
          <header className="px-3 py-2.5 border-b border-slate-100 flex items-center gap-2 bg-slate-50/70">
            {roomId ? (
              <button type="button" onClick={() => setRoomId(0)} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-200/70" title={T("กลับไปรายการห้อง", "Back to chats")}>
                <ArrowLeft className="w-4 h-4" />
              </button>
            ) : (
              <MessageSquare className="w-4 h-4 text-indigo-600 ml-1" />
            )}
            <span className="text-sm font-black text-slate-900 truncate flex-1">
              {room ? room.name : T("แชทภายใน", "Team chat")}
            </span>
            <button
              type="button"
              onClick={toggleMute}
              title={muted ? T("เปิดเสียงเตือน", "Turn sound on") : T("ปิดเสียงเตือน", "Mute sound")}
              className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-200/70"
            >
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
            {notifyPerm === "default" && (
              <button
                type="button"
                onClick={enableDesktop}
                title={T("ให้เด้งแจ้งเตือนบนจอคอม ตอนย่อเบราว์เซอร์ไว้", "Show desktop notifications while the browser is minimized")}
                className="p-1.5 rounded-lg text-amber-600 hover:bg-amber-50"
              >
                <Bell className="w-4 h-4" />
              </button>
            )}
            {notifyPerm === "denied" && (
              <span title={T("เบราว์เซอร์บล็อกการแจ้งเตือนไว้ — เปิดได้ที่ไอคอนแม่กุญแจหน้าลิงก์", "The browser blocks notifications — allow them from the padlock icon by the address")} className="p-1.5 text-slate-300">
                <BellOff className="w-4 h-4" />
              </span>
            )}
            <button
              type="button"
              onClick={() => { setOpen(false); navigate(roomId ? `/chat/${roomId}` : "/chat"); }}
              title={T("เปิดเต็มจอ", "Open full screen")}
              className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-200/70"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
            <button type="button" onClick={() => setOpen(false)} title={T("ย่อลง", "Minimize")} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-200/70">
              <ChevronDown className="w-4 h-4" />
            </button>
          </header>

          {!roomId ? (
            <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
              {rooms.length === 0 && (
                <button
                  type="button"
                  onClick={() => { setOpen(false); navigate("/chat"); }}
                  className="w-full px-3 py-6 text-xs text-slate-400 hover:text-slate-600"
                >
                  {T("ยังไม่มีห้อง — กดที่นี่เพื่อเปิดหน้าแชทแล้วเริ่มคุย", "No chats yet — tap here to open chat and start")}
                </button>
              )}
              {rooms.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setRoomId(r.id)}
                  className="w-full text-left px-3 py-2.5 flex gap-2.5 items-center hover:bg-slate-50"
                >
                  <RoomAvatar room={r} size="w-8 h-8" />
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-bold text-slate-800 truncate">{r.name}</div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {r.preview ? `${r.preview.by ? `${r.preview.by}: ` : ""}${r.preview.text}` : T("ยังไม่มีข้อความ", "No messages yet")}
                    </div>
                  </div>
                  {r.unread > 0 && (
                    <span className="shrink-0 min-w-[1.2rem] h-5 px-1.5 rounded-full bg-rose-500 text-white text-[10px] font-black flex items-center justify-center">
                      {r.unread > 99 ? "99+" : r.unread}
                    </span>
                  )}
                </button>
              ))}
            </div>
          ) : (
            <>
              <div ref={dockListRef} className="flex-1 overflow-y-auto px-3 py-2 bg-slate-50">
                {hasMore && (
                  <div className="text-center mb-1">
                    <button type="button" onClick={loadOlder} className="text-[11px] font-semibold text-indigo-600 hover:underline">
                      {T("โหลดข้อความเก่ากว่านี้", "Load older messages")}
                    </button>
                  </div>
                )}
                {loading && <p className="text-[11px] text-slate-400 text-center py-3">{T("กำลังโหลด…", "Loading…")}</p>}
                {!loading && messages.length === 0 && (
                  <p className="text-[11px] text-slate-400 text-center py-6">{T("ยังไม่มีข้อความ — ทักก่อนเลย", "No messages yet — say hello")}</p>
                )}
                <MessageList messages={messages} myId={myId} onDelete={doDelete} onReply={setReplyTo} memberNames={memberNames} members={members} compact />
                <div ref={bottomRef} />
              </div>

              <footer className="border-t border-slate-100 px-2.5 py-2">
                <ReplyBar reply={replyTo} onClear={() => setReplyTo(null)} />
                {files.length > 0 && (
                  <div className="mb-1.5 flex flex-wrap gap-1">
                    {files.map((f, i) => (
                      <span key={`${f.name}-${i}`} className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-700">
                        <FileText className="w-3 h-3" />
                        <span className="max-w-[8rem] truncate">{f.name}</span>
                        <span className="text-slate-400">{fileSize(f.size)}</span>
                        <button type="button" onClick={() => setFiles((fs) => fs.filter((_, j) => j !== i))} className="text-slate-400 hover:text-rose-600">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex items-end gap-1.5">
                  <label className="p-2 rounded-xl text-slate-500 hover:bg-slate-100 cursor-pointer shrink-0" title={T("แนบไฟล์", "Attach files")}>
                    <Paperclip className="w-4 h-4" />
                    <input
                      type="file"
                      multiple
                      className="hidden"
                      onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
                    />
                  </label>
                  <EmojiPicker compact disabled={sending} onInsert={(e) => setText((t) => t + e)} onQuick={(q) => send(q)} />
                  <MentionTextarea
                    rows={1}
                    value={text}
                    onChange={setText}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                    onPaste={onPaste}
                    members={room?.kind === "GROUP" ? members : []}
                    myId={myId}
                    onPick={(id) => setMentioned((s0) => new Set(s0).add(id))}
                    placeholder={T("พิมพ์ข้อความ… (@ แท็กคน · Ctrl+V วางรูป)", "Type a message… (@ to tag · Ctrl+V to paste images)")}
                    className="w-full resize-none max-h-24 bg-slate-50/70 border border-slate-200 rounded-xl px-3 py-2 text-[13px] text-slate-800 placeholder:text-slate-400 focus:bg-white focus:border-slate-900 outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => send()}
                    disabled={sending || (!text.trim() && !files.length)}
                    className="p-2 rounded-xl bg-slate-900 text-white hover:bg-slate-800 disabled:bg-slate-300 shrink-0"
                    title={T("ส่ง", "Send")}
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </footer>
            </>
          )}
        </div>
      )}
    </>
  );
}
